import moment from '@/moment'
import constant from '@/constant'
import numeral from '@/numeral'
import utils from '@/utils'
import { mapGetters } from 'vuex'

export default {
  data() {
    return {
      isDataLoading: false,
      validationRules: {
        required: (v) => !!v || 'This field is required',
        email: (v) =>
          /\w+@[a-zA-Z0-9._-]+\.[A-Za-z]+/.test(v) || 'E-mail must be valid',
        phoneNumber: (v) =>
          /\d{10,15}/.test(v) ||
          'Phone Number must be valid. Example: 0812344XXXX',
        password: (v) => (v && v.length >= 8) || 'Min 8 characters',
      },
      defaultDataTableFooterProps: {
        'items-per-page-options': constant.itemsPerPageOptions,
      },
    }
  },
  computed: {
    ...mapGetters({
      hasPermission: 'hasPermission',
      isTeacherAccount: 'isTeacherAccount',
      isStudentAccount: 'isStudentAccount',
      isEmployeeAccount: 'isEmployeeAccount',
    }),
    containerClass() {
      return {
        'px-16': this.$vuetify.breakpoint.xlOnly,
        'py-8': !this.$vuetify.breakpoint.xlOnly,
      }
    },
    sectionClass() {
      return {
        'px-16': this.$vuetify.breakpoint.xlOnly,
        'py-16': this.$vuetify.breakpoint.xlOnly,
        'px-8': this.$vuetify.breakpoint.mdAndUp,
        'py-8': !this.$vuetify.breakpoint.xlOnly,
      }
    },
    yearOptions() {
      return utils
        .range(2019, 2045, 1)
        .map((e) => ({ text: e.toString(), value: e }))
    },
    monthOptions() {
      return utils.range(1, 12, 1).map((e) => ({
        text: moment()
          .month(e - 1)
          .format('MMM'),
        value: e,
      }))
    },
    rowsPerPageOptions() {
      return constant.rowsPerPageOptions
    },
    terms() {
      return utils.range(1, 50, 1)
    },
    months() {
      return utils.range(1, 3, 1)
    },
    sessions() {
      return utils.range(1, 16, 1)
    },
  },
  methods: {
    toServerSideQuery({ filters, options }) {
      return {
        ...filters,
        page: options.page ?? 1,
        page_size: options.itemsPerPage ?? 10,
        sort: this.toServerSideSort(this.options),
      }
    },
    toServerSideSort({ sortBy, sortDesc }) {
      if (!sortBy || sortBy.length < 1) {
        return null
      }
      const result = []
      sortBy.forEach(function (val, index) {
        const sortDir = sortDesc[index] ? 'desc' : 'asc'
        result.push(`${val},${sortDir}`)
      })
      return result
    },
    timeFromNow(date) {
      return moment(date).fromNow()
    },
    formatDate(date, pattern = 'YYYY-MM-DD') {
      return date ? moment(date).format(pattern) : null
    },
    formatTime(time, pattern = 'HH:mm') {
      return time ? moment(time, 'HH:mm:ss').format(pattern) : null
    },
    formatDateRangeDisplay(startDate, endDate) {
      const start = moment(startDate)
      const end = moment(endDate)
      if (start.isSame(end, 'year')) {
        if (start.isSame(end, 'month')) {
          // same month format
          return `${start.format('DD')} - ${end.format('DD MMM YYYY')}`
        } else if (start.isSame(end, 'day')) {
          // same day format
          return `${start.format('DD MMM YYYY')}`
        }
        // same year format
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`
      }
      return `${start.format('DD MMM YYYY')} - ${end.format('DD MMM YYYY')}`
    },
    formatNumber(value, pattern = '0,0') {
      return numeral(value).format(pattern)
    },
    parseNumber(value) {
      return numeral(value).value()
    },
    bilingualText(item, prop, propAlt) {
      return item[prop] + ' ' + (item[propAlt] ? `(${item[propAlt]})` : '')
    },
    yesOrNoText(value) {
      return value ? 'Yes' : 'No'
    },
    genderProps(value, prop) {
      const item = constant.genderOptions.find((e) => e.value === value)
      return item ? item[prop] : null
    },
    dayOfWeekProps(value, prop) {
      const item = constant.dayOfWeekOptions.find((e) => e.value === value)
      return item ? item[prop] : null
    },
    studentStatusProps(value, prop) {
      const item = constant.studentStatusOptions.find((e) => e.value === value)
      return item ? item[prop] : null
    },
    teacherStatusProps(value, prop) {
      const item = constant.teacherStatusOptions.find((e) => e.value === value)
      return item ? item[prop] : null
    },
    lessonPlanStatusProps(value, prop) {
      const item = constant.lessonPlanStatusOptions.find(
        (e) => e.value === value
      )
      return item ? item[prop] : null
    },
    classroomTypeProps(value, prop) {
      const item = constant.classroomTypeOptions.find((e) => e.value === value)
      return item ? item[prop] : null
    },
    subscriptionPackageStatusProps(value, prop) {
      const item = constant.subscriptionPackageStatusOptions.find(
        (e) => e.value === value
      )
      return item ? item[prop] : null
    },
    subscriptionOrderStatusProps(value, prop) {
      const item = constant.subscriptionOrderStatusOptions.find(
        (e) => e.value === value
      )
      return item ? item[prop] : null
    },
    invoiceStatusProps(value, prop) {
      const item = constant.invoiceStatusOptions.find((e) => e.value === value)
      return item ? item[prop] : null
    },
    classroomStatusProps(value, prop) {
      const item = constant.classroomStatusOptions.find(
        (e) => e.value === value
      )
      return item ? item[prop] : null
    },
    scoreGradeProps(value, prop) {
      const item = constant.scoreGradeOptions.find((e) => e.value === value)
      return item ? item[prop] : null
    },
    payrollStatusProps(value, prop) {
      const item = constant.payrollStatusOptions.find((e) => e.value === value)
      return item ? item[prop] : null
    },
    holidayStatusProps(value, prop) {
      const item = constant.holidayStatusOptions.find((e) => e.value === value)
      return item ? item[prop] : null
    },
    filenameFromUrl(url) {
      return url.substring(url.lastIndexOf('/') + 1)
    },
    timeDiffText(start, end) {
      const startTime = moment(start, 'HH:mm:ss')
      const endTime = moment(end, 'HH:mm:ss')
      const diffHours = endTime.diff(startTime, 'hours', true)
      if (diffHours < 1) {
        const diffMinutes = endTime.diff(startTime, 'minutes', true)
        return `${diffMinutes} minutes`
      }
      return `${diffHours} hours`
    },
  },
}
